"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { Box, Dialog, Grid, IconButton, InputBase, Tooltip, Typography } from "@mui/material";
import { BadgeCheck, Hash, Pencil, Plus, Save, Search, ShieldCheck, Trash2, Truck, Users, X } from "lucide-react";
import swal2 from "sweetalert2";

import { useDialog } from "../AdminConfirmDialogProvider";
import AppButton from "../../ui/AppButton";
import AppSearch from "../../ui/AppSearch";
import palette from "../../../theme/palette";

import "./common/trDataTableTheme";

const fieldSx = {
  minHeight: 40,
  px: 1.15,
  display: "flex",
  alignItems: "center",
  backgroundColor: palette.bg,
  border: `1px solid ${palette.border}`,
  borderRadius: 2,
  transition: "border-color .18s ease, background-color .18s ease",
  "&:focus-within": {
    borderColor: palette.accent,
    backgroundColor: palette.surfaceAlt,
  },
};

const inputSx = {
  color: palette.text,
  fontSize: "13px",
  width: "100%",
  "& input::placeholder": { color: palette.muted, opacity: 1 },
};

const customStyles = {
  tableWrapper: { style: { borderRadius: palette.radius.listCard, overflow: "hidden", border: `1px solid ${palette.borderSoft}`, backgroundColor: palette.surface } },
  responsiveWrapper: { style: { borderRadius: palette.radius.listCard } },
  table: { style: { backgroundColor: palette.surface } },
  headRow: { style: { backgroundColor: palette.surfaceAlt, color: palette.muted, borderBottom: `1px solid ${palette.borderSoft}` } },
  headCells: { style: { color: palette.muted, fontSize: "11px", fontWeight: 800, textTransform: "uppercase" } },
  rows: { style: { backgroundColor: palette.surface, color: palette.text, borderBottom: `1px solid ${palette.borderSoft}` } },
  pagination: { style: { backgroundColor: palette.surface, color: palette.muted, borderTop: `1px solid ${palette.borderSoft}`, borderRadius: `0 0 ${palette.radius.listCard} ${palette.radius.listCard}` } },
};

const actionButtonSx = (danger = false) => ({
  width: 30,
  height: 30,
  borderRadius: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: palette.chip,
  border: `1px solid ${palette.border}`,
  color: palette.muted,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: danger ? "#c2410c" : palette.accent,
    borderColor: danger ? "#c2410c" : palette.accent,
    color: "#fff",
  },
});

const stickyActionColumnSx = {
  position: "sticky",
  right: 0,
  zIndex: 2,
  backgroundColor: palette.surface,
  boxShadow: "-10px 0 16px rgba(0,0,0,.16)",
};

function Field({ label, icon, children }) {
  return (
    <Box>
      <Typography
        sx={{
          color: palette.muted,
          fontSize: "10.5px",
          fontWeight: 800,
          textTransform: "uppercase",
          mb: 0.4,
        }}
      >
        {label}
      </Typography>
      <Box sx={fieldSx}>
        {icon && (
          <Box sx={{ color: palette.muted, display: "flex", alignItems: "center", mr: 0.8, flexShrink: 0 }}>
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Box>
    </Box>
  );
}

function CaptureInput({ value, onChange, placeholder, readOnly = false, type = "text", inputMode, align = "left" }) {
  return (
    <InputBase
      type={type}
      value={value || ""}
      readOnly={readOnly}
      placeholder={placeholder}
      inputProps={{ inputMode }}
      onChange={(event) => onChange(event.target.value)}
      sx={{ ...inputSx, "& input": { textAlign: align } }}
    />
  );
}

function PlacaModal({ open, placa, onClose, onSubmit }) {
  const [draft, setDraft] = useState({
    placa: "",
    marca: "",
    certificado: "",
    asientos: "",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setDraft({
        placa: placa?.placa || "",
        marca: placa?.marca || "",
        certificado: placa?.certificado || "",
        asientos: placa?.asientos ?? "",
      });
      setError("");
    }
  }, [open, placa]);

  const updateDraft = (name, value) => setDraft((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = () => {
    if (!draft.placa) {
      setError("Completa la placa.");
      return;
    }
    onSubmit({
      ...draft,
      placa: String(draft.placa || "").trim().toUpperCase(),
      marca: String(draft.marca || "").trim().toUpperCase(),
      certificado: String(draft.certificado || "").trim().toUpperCase(),
      asientos: draft.asientos === "" || draft.asientos === null || draft.asientos === undefined ? null : Number(draft.asientos),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: palette.surface,
          color: palette.text,
          border: `1px solid ${palette.border}`,
          borderRadius: 3,
        },
      }}
    >
      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5, mb: 1.6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: palette.accentSoft,
                color: palette.accent,
                flexShrink: 0,
              }}
            >
              <Truck size={18} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: "16px", md: "17px" }, lineHeight: 1.2 }}>
                {placa ? "Editar placa" : "Nueva placa"}
              </Typography>
              <Typography sx={{ color: palette.muted, fontSize: "11.5px", mt: 0.25 }}>
                Unidades habilitadas para operaciones de transporte.
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: palette.muted, mt: -0.5 }}>
            <X size={18} />
          </IconButton>
        </Box>

        <Grid container spacing={1.15}>
          <Grid item xs={12} md={4}>
            <Field label="Placa" icon={<Hash size={15} />}>
              <CaptureInput
                value={draft.placa}
                onChange={(value) => updateDraft("placa", value.toUpperCase())}
                placeholder="V1A-123"
                readOnly={Boolean(placa)}
              />
            </Field>
          </Grid>
          <Grid item xs={12} md={4}>
            <Field label="Marca" icon={<Truck size={15} />}>
              <CaptureInput value={draft.marca} onChange={(value) => updateDraft("marca", value.toUpperCase())} placeholder="VOLVO" />
            </Field>
          </Grid>
          <Grid item xs={12} md={4}>
            <Field label="Certificado" icon={<ShieldCheck size={15} />}>
              <CaptureInput value={draft.certificado} onChange={(value) => updateDraft("certificado", value.toUpperCase())} placeholder="CERT-001" />
            </Field>
          </Grid>
          <Grid item xs={12} md={4}>
            <Field label="Asientos" icon={<Users size={15} />}>
              <CaptureInput
                value={draft.asientos}
                onChange={(value) => updateDraft("asientos", value.replace(/\D/g, ""))}
                placeholder="18"
                type="number"
                inputMode="numeric"
                align="right"
              />
            </Field>
          </Grid>
        </Grid>

        {error && <Typography sx={{ color: "#ff8a65", fontSize: "12px", mt: 1 }}>{error}</Typography>}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.8, mt: 1.8, flexWrap: "wrap" }}>
          <AppButton onClick={onClose}>Cancelar</AppButton>
          <AppButton icon={<Save size={16} />} onClick={handleSubmit} sx={{ backgroundColor: palette.accent, borderColor: palette.accent, color: palette.surface, fontWeight: 800 }}>
            Guardar
          </AppButton>
        </Box>
      </Box>
    </Dialog>
  );
}

export default function TrPlacaList() {
  const back_host = process.env.BACK_HOST || "https://xpertcont-backend-js-production-50e6.up.railway.app";
  const params = useParams();
  const { confirmDialog } = useDialog();
  const [rows, setRows] = useState([]);
  const [baseRows, setBaseRows] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${back_host}/mve_transplaca/${params.id_anfitrion}/${params.documento_id}`);
      const result = await response.json();
      const data = Array.isArray(result?.data) ? result.data : [];
      setRows(data);
      setBaseRows(data);
    } catch (error) {
      console.log("Error cargando placas:", error);
      setRows([]);
      setBaseRows([]);
    } finally {
      setLoading(false);
    }
  }, [back_host, params.id_anfitrion, params.documento_id]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const filtrar = (texto) => {
    const value = texto.toLowerCase();
    setRows(baseRows.filter((item) => [
      item.placa,
      item.marca,
      item.certificado,
      item.asientos,
    ].some((field) => String(field || "").toLowerCase().includes(value))));
  };

  const guardar = async (draft) => {
    const esEdicion = Boolean(editando);
    try {
      const response = await fetch(`${back_host}/mve_transplaca`, {
        method: esEdicion ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...draft,
          id_anfitrion: params.id_anfitrion,
          documento_id: params.documento_id,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || "No se pudo guardar la placa.");
      }
      setModalOpen(false);
      setEditando(null);
      cargarDatos();
    } catch (error) {
      swal2.fire({ title: "No se pudo guardar", text: error.message || "Error interno.", icon: "error", confirmButtonText: "ACEPTAR" });
    }
  };

  const eliminar = async (row) => {
    const result = await confirmDialog({
      title: "Eliminar placa?",
      message: `${row.placa}${row.marca ? ` - ${row.marca}` : ""}`,
      icon: "warning",
      confirmText: "ELIMINAR",
      cancelText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${back_host}/mve_transplaca/${params.id_anfitrion}/${params.documento_id}/${row.placa}`, { method: "DELETE" });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || "No se pudo eliminar la placa.");
      }
      cargarDatos();
    } catch (error) {
      swal2.fire({ title: "No se pudo eliminar", text: error.message || "Error interno.", icon: "error", confirmButtonText: "ACEPTAR" });
    }
  };

  const columns = useMemo(() => [
    {
      name: "Placa",
      selector: row => row.placa,
      width: "160px",
      cell: row => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.85, fontWeight: 800 }}>
          <BadgeCheck size={15} color={palette.accent} />
          {row.placa}
        </Box>
      ),
    },
    { name: "Marca", selector: row => row.marca || "-", grow: 1 },
    { name: "Certificado", selector: row => row.certificado || "-", grow: 1 },
    {
      name: "Asientos",
      selector: row => row.asientos ?? "-",
      width: "120px",
      right: true,
      cell: row => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5, width: "100%", fontWeight: 800 }}>
          <Users size={14} color={palette.muted} />
          {row.asientos ?? "-"}
        </Box>
      ),
    },
    {
      name: "",
      width: "90px",
      right: true,
      style: stickyActionColumnSx,
      cell: row => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.7, width: "100%" }}>
          <Tooltip title="Editar" arrow>
            <Box sx={actionButtonSx()} onClick={() => { setEditando(row); setModalOpen(true); }}>
              <Pencil size={14} />
            </Box>
          </Tooltip>
          <Tooltip title="Eliminar" arrow>
            <Box sx={actionButtonSx(true)} onClick={() => eliminar(row)}>
              <Trash2 size={14} />
            </Box>
          </Tooltip>
        </Box>
      ),
    },
  ], []);

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: "transparent", p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 980, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 1.2, mb: 2, flexDirection: { xs: "column", sm: "row" } }}>
          <Box>
            <Typography sx={{ color: palette.text, fontWeight: 800, fontSize: "22px", lineHeight: 1.2 }}>Placas</Typography>
            <Typography sx={{ color: palette.muted, fontSize: "13px", mt: 0.5 }}>{rows.length} placas registradas</Typography>
          </Box>
          <AppSearch placeholder="Buscar placa..." value={busqueda} onChange={(event) => { setBusqueda(event.target.value); filtrar(event.target.value); }} />
          <AppButton icon={<Plus size={18} />} onClick={() => { setEditando(null); setModalOpen(true); }} sx={{ backgroundColor: palette.accent, borderColor: palette.accent, color: palette.surface, fontWeight: 800 }}>
            Nueva placa
          </AppButton>
        </Box>

        <DataTable
          theme="transportesDark"
          columns={columns}
          data={rows}
          progressPending={loading}
          pagination
          paginationPerPage={12}
          customStyles={customStyles}
          noDataComponent={<Box sx={{ py: 4, color: palette.muted, display: "flex", alignItems: "center", gap: 1 }}><Search size={16} />Sin placas registradas</Box>}
        />
      </Box>

      <PlacaModal open={modalOpen} placa={editando} onClose={() => { setModalOpen(false); setEditando(null); }} onSubmit={guardar} />
    </Box>
  );
}
